// =============================================================
// mapLayerEngine.js — apply catalog layers to a Mapbox GL map
// -------------------------------------------------------------
// Pure (non-React) helpers that mutate a live Mapbox GL map instance based on
// the declarative catalog in constants/mapLayers.js. Kept framework-agnostic so
// both MapView and ParcelsMapTab can drive the same layer system.
//
// Basemaps are applied with map.setStyle():
//   • 'mapbox-style' → the mapbox:// style URL directly
//   • 'raster-xyz'   → a minimal style object wrapping a single raster source
// After a style swap all sources/layers are wiped, so callers must re-add their
// own layers (overlays, parcels, markers) on the map's 'style.load' event.
// =============================================================

import { BASEMAP_BY_ID } from '../constants/mapLayers';

const OVERLAY_SRC = (id) => `overlay-src-${id}`;
const OVERLAY_LYR = (id) => `overlay-${id}`;

const DEM_SOURCE = 'mapbox-dem';
const HILLSHADE_LAYER = 'hillshade-3dep';

// ── Basemap ──────────────────────────────────────────────────

// Build a complete Mapbox GL style object for a raster-XYZ basemap. Glyphs are
// borrowed from Mapbox so any symbol overlays added later can still render text.
function rasterStyle(basemap) {
  return {
    version: 8,
    glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
    sources: {
      'basemap-raster': {
        type: 'raster',
        tiles: basemap.tiles,
        tileSize: 256,
        maxzoom: basemap.maxzoom || 22,
        attribution: basemap.attribution || '',
      },
    },
    layers: [
      { id: 'basemap-raster', type: 'raster', source: 'basemap-raster' },
    ],
  };
}

// Swap the active basemap. Returns true if a style change was issued.
export function applyBasemap(map, basemapId) {
  const basemap = BASEMAP_BY_ID[basemapId];
  if (!map || !basemap || basemap.status !== 'live') return false;
  if (basemap.type === 'mapbox-style') {
    map.setStyle(basemap.style);
  } else if (basemap.type === 'raster-xyz') {
    map.setStyle(rasterStyle(basemap));
  } else {
    return false;
  }
  return true;
}

// ── Overlays (raster XYZ / WMS) ──────────────────────────────

export function addOverlay(map, overlay, opacity) {
  if (!map || !overlay) return;
  const srcId = OVERLAY_SRC(overlay.id);
  const lyrId = OVERLAY_LYR(overlay.id);
  if (map.getLayer(lyrId)) return; // already present

  // A single templated URL (WMS GetMap / ArcGIS export, with a {bbox-epsg-3857}
  // token) or an explicit {z}/{x}/{y} tile list both reduce to a tiles array.
  const tiles = overlay.tiles || (overlay.url ? [overlay.url] : null);
  if (!tiles || !tiles.length) return;

  if (!map.getSource(srcId)) {
    map.addSource(srcId, {
      type: 'raster',
      tiles,
      tileSize: overlay.tileSize || 256,
      maxzoom: overlay.maxzoom || 22,
      attribution: overlay.attribution || '',
    });
  }
  map.addLayer({
    id: lyrId,
    type: 'raster',
    source: srcId,
    paint: {
      'raster-opacity': opacity != null ? opacity : (overlay.defaultOpacity ?? 0.7),
    },
  });
}

export function removeOverlay(map, overlay) {
  if (!map || !overlay) return;
  const srcId = OVERLAY_SRC(overlay.id);
  const lyrId = OVERLAY_LYR(overlay.id);
  if (map.getLayer(lyrId))  map.removeLayer(lyrId);
  if (map.getSource(srcId)) map.removeSource(srcId);
}

export function setOverlayOpacity(map, overlay, opacity) {
  if (!map || !overlay) return;
  const lyrId = OVERLAY_LYR(overlay.id);
  if (map.getLayer(lyrId)) map.setPaintProperty(lyrId, 'raster-opacity', opacity);
}

// Re-add a set of active overlays in one call (used after a basemap swap).
// `active` is a map of overlayId → { overlay, opacity, on }.
export function reapplyOverlays(map, active) {
  if (!map || !active) return;
  Object.values(active).forEach(({ overlay, opacity, on }) => {
    if (on) addOverlay(map, overlay, opacity);
  });
}

// ── 3D terrain + hillshade (Mapbox terrain-DEM) ──────────────
// Doubles as the "LIDAR / HD Topo" and "elevation exaggeration" controls — the
// Mapbox terrain-RGB DEM is a high-resolution elevation model.

export function enableTerrain(map, exaggeration = 1.4) {
  if (!map) return;
  if (!map.getSource(DEM_SOURCE)) {
    map.addSource(DEM_SOURCE, {
      type: 'raster-dem',
      url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
      tileSize: 512,
      maxzoom: 14,
    });
  }
  map.setTerrain({ source: DEM_SOURCE, exaggeration });

  // Subtle hillshade for relief shading on flatter basemaps.
  if (!map.getLayer(HILLSHADE_LAYER)) {
    map.addLayer({
      id: HILLSHADE_LAYER,
      type: 'hillshade',
      source: DEM_SOURCE,
      paint: { 'hillshade-exaggeration': 0.4 },
    });
  }
}

export function setTerrainExaggeration(map, exaggeration) {
  if (!map || !map.getSource(DEM_SOURCE)) return;
  map.setTerrain({ source: DEM_SOURCE, exaggeration });
}

export function disableTerrain(map) {
  if (!map) return;
  map.setTerrain(null);
  if (map.getLayer(HILLSHADE_LAYER)) map.removeLayer(HILLSHADE_LAYER);
  if (map.getSource(DEM_SOURCE))     map.removeSource(DEM_SOURCE);
}
